import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormHelperText,
    FormLabel, Input, Modal, ModalDialog, Option, Select, Sheet, Stack,
    Switch, Table, Tab, TabList, TabPanel, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsCampaigns, APIAdsRuleCreate, APIAdsRuleDelete, APIAdsRuleLogs,
    APIAdsRuleUpdate, APIAdsRules, adsErrorText,
} from '../../../api_ads';
import { formatMoney } from '../../../components/Ads/adsConstants';


const EMPTY_RULE = {
    name: '', campaign: null,
    metric: 'ctr', operator: 'lt', threshold: 0.5,
    window_days: 7, min_impressions: 1000,
    action: 'notify', action_value: 10,
    cooldown_hours: 24, is_enabled: true,
};

const TEMPLATES = [
    {
        label: 'Уведомить о низком CTR',
        patch: { name: 'Низкий CTR', metric: 'ctr', operator: 'lt',
                 threshold: 0.5, action: 'notify', min_impressions: 2000 },
    },
    {
        label: 'Остановить при дорогом клике',
        patch: { name: 'Дорогой клик', metric: 'cpc', operator: 'gt',
                 threshold: 50, action: 'pause', min_impressions: 1000 },
    },
    {
        label: 'Поднять ставку при малом расходе',
        patch: { name: 'Мало расхода', metric: 'spend', operator: 'lt',
                 threshold: 500, action: 'bid_up', action_value: 15, min_impressions: 500 },
    },
];


function RuleModal({ open, onClose, rule, campaigns, dictionaries, onSaved }) {
    const { advertiserToken: token } = useAdvertiser();
    const [form, setForm] = useState(EMPTY_RULE);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const isEdit = !!rule?.id;

    useEffect(() => {
        if (!open) return;
        setForm(isEdit ? { ...EMPTY_RULE, ...rule } : EMPTY_RULE);
        setError(null);
    }, [open, rule, isEdit]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const submit = async () => {
        if (!form.name.trim()) { setError('Укажите название правила'); return; }
        if (!form.min_impressions || Number(form.min_impressions) < 1) {
            setError('Порог показов обязателен: без него правило сработает на случайных данных');
            return;
        }
        const payload = {
            name: form.name.trim(),
            campaign: form.campaign || null,
            metric: form.metric,
            operator: form.operator,
            threshold: Number(form.threshold),
            window_days: Number(form.window_days),
            min_impressions: Number(form.min_impressions),
            action: form.action,
            action_value: Number(form.action_value) || 0,
            cooldown_hours: Number(form.cooldown_hours) || 24,
            is_enabled: !!form.is_enabled,
        };
        try {
            setSaving(true);
            setError(null);
            if (isEdit) await APIAdsRuleUpdate(token, rule.id, payload);
            else await APIAdsRuleCreate(token, payload);
            onSaved?.();
            onClose?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setSaving(false);
        }
    };

    const isBidAction = ['bid_up', 'bid_down'].includes(form.action);

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ width: { xs: '94vw', sm: 560 }, maxHeight: '92vh', overflowY: 'auto' }}>
                <Typography level="title-lg">
                    {isEdit ? 'Изменить правило' : 'Новое автоправило'}
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Правила проверяются раз в час по данным статистики
                </Typography>

                {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

                {!isEdit && (
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        {TEMPLATES.map((template) => (
                            <Chip
                                key={template.label}
                                variant="outlined"
                                onClick={() => set(template.patch)}
                                sx={{ cursor: 'pointer' }}
                            >
                                {template.label}
                            </Chip>
                        ))}
                    </Stack>
                )}

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Название</FormLabel>
                        <Input value={form.name} onChange={(e) => set({ name: e.target.value })} />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Применять к</FormLabel>
                        <Select
                            value={form.campaign ?? ''}
                            onChange={(_, v) => set({ campaign: v || null })}
                        >
                            <Option value="">Всем кампаниям</Option>
                            {campaigns.map((c) => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                        <FormControl sx={{ flex: 2 }}>
                            <FormLabel>Показатель</FormLabel>
                            <Select value={form.metric} onChange={(_, v) => v && set({ metric: v })}>
                                {(dictionaries?.metrics || []).map((m) => (
                                    <Option key={m.value} value={m.value}>{m.label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Условие</FormLabel>
                            <Select value={form.operator} onChange={(_, v) => v && set({ operator: v })}>
                                {(dictionaries?.operators || []).map((o) => (
                                    <Option key={o.value} value={o.value}>{o.label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Значение</FormLabel>
                            <Input
                                type="number"
                                value={form.threshold}
                                onChange={(e) => set({ threshold: e.target.value })}
                            />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Окно, дней</FormLabel>
                            <Input
                                type="number"
                                value={form.window_days}
                                onChange={(e) => set({ window_days: e.target.value })}
                            />
                            <FormHelperText>за какой период считать</FormHelperText>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Минимум показов</FormLabel>
                            <Input
                                type="number"
                                value={form.min_impressions}
                                onChange={(e) => set({ min_impressions: e.target.value })}
                            />
                            <FormHelperText>
                                без порога три показа с нулём кликов остановят кампанию
                            </FormHelperText>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                        <FormControl sx={{ flex: 2 }}>
                            <FormLabel>Действие</FormLabel>
                            <Select value={form.action} onChange={(_, v) => v && set({ action: v })}>
                                {(dictionaries?.actions || []).map((a) => (
                                    <Option key={a.value} value={a.value}>{a.label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        {isBidAction && (
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>На сколько, %</FormLabel>
                                <Input
                                    type="number"
                                    value={form.action_value}
                                    onChange={(e) => set({ action_value: e.target.value })}
                                />
                                <FormHelperText>от 1 до 100</FormHelperText>
                            </FormControl>
                        )}
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Пауза, часов</FormLabel>
                            <Input
                                type="number"
                                value={form.cooldown_hours}
                                onChange={(e) => set({ cooldown_hours: e.target.value })}
                            />
                            <FormHelperText>между срабатываниями</FormHelperText>
                        </FormControl>
                    </Stack>

                    {form.action === 'pause' && (
                        <Alert color="warning" size="sm">
                            Правило остановит показы. Кампанию придётся запускать вручную —
                            автоматически она не возобновится.
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" color="neutral" fullWidth
                                onClick={onClose} disabled={saving}>
                            Отмена
                        </Button>
                        <Button fullWidth loading={saving} onClick={submit}>
                            {isEdit ? 'Сохранить' : 'Создать правило'}
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


export default function AdsRulesPage() {
    const { advertiserToken: token } = useAdvertiser();

    const [items, setItems] = useState([]);
    const [dictionaries, setDictionaries] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState(0);
    const [modal, setModal] = useState({ open: false, rule: null });

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const [rulesRes, campaignsRes, logsRes] = await Promise.all([
                APIAdsRules(token),
                APIAdsCampaigns(token, { pageSize: 100, hideArchived: true }),
                APIAdsRuleLogs(token, { pageSize: 50 }),
            ]);
            setItems(rulesRes?.items || []);
            setDictionaries({
                metrics: rulesRes?.metrics || [],
                operators: rulesRes?.operators || [],
                actions: rulesRes?.actions || [],
            });
            setCampaigns(campaignsRes?.items || []);
            setLogs(logsRes?.items || []);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить правила'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (rule) => {
        setBusy(`toggle-${rule.id}`);
        try {
            await APIAdsRuleUpdate(token, rule.id, { is_enabled: !rule.is_enabled });
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally { setBusy(null); }
    };

    const remove = async (rule) => {
        setBusy(`del-${rule.id}`);
        try {
            await APIAdsRuleDelete(token, rule.id);
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally { setBusy(null); }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4">Автоправила</Typography>
                    <Typography level="body-sm" color="neutral">
                        Автоматические действия по условиям — проверяются раз в час
                    </Typography>
                </Box>
                <Button startDecorator={<AddRoundedIcon />}
                        onClick={() => setModal({ open: true, rule: null })}>
                    Создать правило
                </Button>
            </Stack>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Правила ({items.length})</Tab>
                    <Tab>Срабатывания ({logs.length})</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0 }}>
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : items.length === 0 ? (
                        <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="title-md" sx={{ mb: 0.5 }}>Правил пока нет</Typography>
                            <Typography level="body-sm" color="neutral">
                                Правила помогают не следить за кампаниями вручную:
                                система сама уведомит о проблеме или скорректирует ставку.
                            </Typography>
                        </Sheet>
                    ) : (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                            <Table sx={{ '--TableCell-paddingY': '10px' }}>
                                <thead>
                                    <tr>
                                        <th>Правило</th>
                                        <th style={{ width: 240 }}>Условие</th>
                                        <th style={{ width: 190 }}>Действие</th>
                                        <th style={{ width: 130 }}>Срабатываний</th>
                                        <th style={{ width: 100 }}>Активно</th>
                                        <th style={{ width: 130 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((rule) => (
                                        <tr key={rule.id}>
                                            <td>
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {rule.name}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {rule.campaign_name || 'все кампании'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {rule.metric_display} {rule.operator_display}{' '}
                                                    {rule.threshold}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    за {rule.window_days} дн.,
                                                    от {rule.min_impressions} показов
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {rule.action_display}
                                                    {['bid_up', 'bid_down'].includes(rule.action)
                                                        ? ` на ${rule.action_value}%` : ''}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {rule.triggered_count || 0}
                                                </Typography>
                                                {rule.last_triggered_at && (
                                                    <Typography level="body-xs" color="neutral">
                                                        {format(new Date(rule.last_triggered_at),
                                                            'd MMM, HH:mm', { locale: ru })}
                                                    </Typography>
                                                )}
                                            </td>
                                            <td>
                                                <Switch
                                                    checked={!!rule.is_enabled}
                                                    disabled={busy === `toggle-${rule.id}`}
                                                    onChange={() => toggle(rule)}
                                                />
                                            </td>
                                            <td>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Button size="sm" variant="soft"
                                                            onClick={() => setModal({ open: true, rule })}>
                                                        Изменить
                                                    </Button>
                                                    <Button size="sm" variant="plain" color="danger"
                                                            loading={busy === `del-${rule.id}`}
                                                            onClick={() => remove(rule)}>
                                                        <DeleteOutlineRoundedIcon fontSize="small" />
                                                    </Button>
                                                </Stack>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0 }}>
                    {logs.length === 0 ? (
                        <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="body-sm" color="neutral">
                                Правила ещё не срабатывали
                            </Typography>
                        </Sheet>
                    ) : (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                            <Table sx={{ '--TableCell-paddingY': '10px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 170 }}>Когда</th>
                                        <th style={{ width: 200 }}>Правило</th>
                                        <th style={{ width: 220 }}>Кампания</th>
                                        <th style={{ width: 130 }}>Значение</th>
                                        <th>Что сделано</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.id}>
                                            <td>
                                                <Typography level="body-xs">
                                                    {log.date_create
                                                        ? format(new Date(log.date_create),
                                                            'd MMM yyyy, HH:mm', { locale: ru })
                                                        : '—'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{log.rule_name}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{log.campaign_name}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {Number(log.metric_value).toFixed(2)}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                    <Chip
                                                        size="sm"
                                                        variant="soft"
                                                        color={log.action_taken === 'pause'
                                                            ? 'danger' : 'primary'}
                                                    >
                                                        {log.action_display}
                                                    </Chip>
                                                    <Typography level="body-xs" color="neutral">
                                                        {log.result}
                                                    </Typography>
                                                </Stack>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </TabPanel>
            </Tabs>

            <RuleModal
                open={modal.open}
                rule={modal.rule}
                campaigns={campaigns}
                dictionaries={dictionaries}
                onClose={() => setModal({ open: false, rule: null })}
                onSaved={load}
            />
        </Box>
    );
}